import { useCallback, useState } from "react";
import type { Location } from "history";
import { defer, throttle } from "lodash";
import { isMobileSafari } from "react-device-detect";
import { useQueryClient } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import { usePostAnalytics } from "@/react/components/AnalyticsTracker/usePostAnalytics";
import { HOME_PAGE_POST_REQUEST_KEY } from "@circle-react/components/HomePageV2/usePostList";
import {
  CurrentPostContextProvider,
  CurrentSpaceContextProvider,
} from "@circle-react/contexts";
import { PostsDetailsContextProvider } from "@circle-react/contexts/postsDetailsContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { Post } from "@circle-react/types";
import { Modal } from "@circle-react-shared/uikit/ModalV2";
import { useShowPost } from "../Show/useShowPost";
import { ModalBody } from "./ModalBody";
import { ModalHeader } from "./ModalHeader";
import { Navigation } from "./Navigation";

const HEADER_SCROLL_THRESHOLD = 50;

interface LocationState {
  currentPage?: number;
  isWithinProfileContext?: boolean;
  params: Record<string, unknown>;
  postList?: Post[];
  prevLocation?: Location<LocationState>;
  profileContextMember?: {
    id: number;
    public_uid: string;
  };
  shouldDisableNavigation?: boolean;
}

export const PostModalComponent = () => {
  const [isNavigatingInsidePostModal, setIsNavigatingInsidePostModal] =
    useState(false);
  const [shouldCompactHeader, setShouldCompactHeader] = useState(false);
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const {
    isLoading: isOpeningPostModal,
    postResource,
    perPagePostIds,
    space,
  } = useShowPost();
  const { state } = location;
  const {
    prevLocation,
    postList: initialPostList,
    currentPage: initialPage = 1,
    params,
    isWithinProfileContext,
    profileContextMember,
    shouldDisableNavigation = false,
  } = state ?? {};

  const wasInFeed = prevLocation?.pathname === "/feed";
  const shouldDisplayLoader = isOpeningPostModal || isNavigatingInsidePostModal;
  const queryClient = useQueryClient();
  const postListQueryKey = wasInFeed
    ? [HOME_PAGE_POST_REQUEST_KEY]
    : ["posts", space?.id];

  const invalidateQueries = useCallback(() => {
    void queryClient.invalidateQueries(postListQueryKey);
  }, [queryClient, postListQueryKey]);

  const deferredInvalidateQueries = useCallback(() => {
    defer(invalidateQueries);
  }, [invalidateQueries]);

  const handleScroll = useCallback(
    throttle((scrollTop: number) => {
      setShouldCompactHeader(scrollTop > HEADER_SCROLL_THRESHOLD);
    }, 500),
    [setShouldCompactHeader],
  );

  const handleIsNavigatingInsidePostModal = useCallback(
    (value: boolean) => {
      setIsNavigatingInsidePostModal(value);
      setShouldCompactHeader(false);
    },
    [setIsNavigatingInsidePostModal, setShouldCompactHeader],
  );

  usePostAnalytics({
    postId: postResource.data?.id,
    spaceId: postResource.data?.space_id,
  });
  return (
    <CurrentSpaceContextProvider spaceId={space?.id}>
      <PostsDetailsContextProvider perPagePostIds={perPagePostIds}>
        <CurrentPostContextProvider
          post={postResource.data}
          onPostChange={postResource.onPostChange}
          usedIn="minimal-post-modal"
        >
          <Modal
            isOpen
            onClose={() => {
              if (prevLocation) {
                history.push(prevLocation);
                deferredInvalidateQueries();
              }
            }}
            contentPortalId="post-modal-portal"
          >
            {!shouldDisableNavigation && (
              <Navigation
                initialPostList={initialPostList}
                initialPage={initialPage}
                currentPost={postResource?.data}
                prevLocation={prevLocation}
                params={params}
                isNavigatingInsidePostModal={isNavigatingInsidePostModal}
                setIsNavigatingInsidePostModal={
                  handleIsNavigatingInsidePostModal
                }
                wasInFeed={wasInFeed}
                isLoading={shouldDisplayLoader}
                isWithinProfileContext={isWithinProfileContext}
                profileContextMember={profileContextMember}
              />
            )}
            <Modal.Overlay />
            <Modal.Content
              size="xl"
              className={classNames("sm:!my-[5vh]", {
                "sm:!max-h-[85vh]": isMobileSafari,
                "sm:!max-h-[90vh]": !isMobileSafari,
              })}
            >
              <ModalHeader
                spaceId={space?.id}
                onChange={postResource?.onPostChange}
                isLoading={shouldDisplayLoader}
                invalidateQueries={deferredInvalidateQueries}
                onPostDestroy={() => {
                  if (prevLocation) {
                    history.push(prevLocation);
                  }
                }}
                compactMode={shouldCompactHeader}
              />
              <ModalBody
                isLoading={shouldDisplayLoader}
                isRenderedOutsideTheSpace={wasInFeed}
                onPostDestroy={() => {
                  if (prevLocation) {
                    history.push(prevLocation);
                  }
                }}
                onScroll={handleScroll}
              />
            </Modal.Content>
          </Modal>
        </CurrentPostContextProvider>
      </PostsDetailsContextProvider>
    </CurrentSpaceContextProvider>
  );
};
